/**
 * Video Management API Service
 * This service handles communication with the Azure Functions backend for video management.
 */

import axios from 'axios';

class VideoService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE;
    console.log('VideoService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get all videos with optional filtering
   * @param {Object} filters - Filter parameters
   * @returns {Promise} - API response with videos
   */
  async getVideos(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.courseId) params.append('courseId', filters.courseId);
      if (filters.category) params.append('category', filters.category);
      if (filters.rank) params.append('rank', filters.rank);
      if (filters.search) params.append('search', filters.search);

      const url = `${this.baseURL}/video/get${params.toString() ? '?' + params.toString() : ''}`;
      console.log(`Getting videos from: ${url}`);
      
      const response = await axios.get(url);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error fetching videos:', error);
      throw error;
    }
  }

  /**
   * Create a new video
   * @param {Object} videoData - Video data object
   * @returns {Promise} - API response
   */
  async createVideo(videoData) {
    try {
      console.log('Creating video:', videoData);
      
      const response = await axios.post(`${this.baseURL}/video/post`, videoData);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error creating video:', error);
      throw error;
    }
  }

  /**
   * Update an existing video
   * @param {string} videoId - Video ID
   * @param {Object} updateData - Updated video data
   * @returns {Promise} - API response
   */
  async updateVideo(videoId, updateData) {
    try {
      console.log(`Updating video ${videoId}:`, updateData);
      
      const response = await axios.put(`${this.baseURL}/video/update?id=${videoId}`, updateData);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error updating video:', error);
      throw error;
    }
  }

  /**
   * Delete a video
   * @param {string} videoId - Video ID
   * @returns {Promise} - API response
   */
  async deleteVideo(videoId) {
    try {
      console.log(`Deleting video ${videoId}`);
      
      const response = await axios.delete(`${this.baseURL}/video/delete?id=${videoId}`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  }

  /**
   * Get video by ID
   * @param {string} videoId - Video ID
   * @returns {Promise} - API response with video data
   */
  async getVideoById(videoId) {
    try {
      const response = await this.getVideos();
      const videos = Array.isArray(response) ? response : response?.data || [];
      const video = videos.find(v => v.id === videoId);
      
      if (!video) {
        throw new Error('Video not found');
      }
      
      return video;
    } catch (error) {
      console.error('Error fetching video by ID:', error);
      throw error;
    }
  }

  /**
   * Get videos by category
   * @param {string} category - Category name
   * @returns {Promise} - API response with filtered videos
   */
  async getVideosByCategory(category) {
    try {
      return await this.getVideos({ category });
    } catch (error) {
      console.error('Error fetching videos by category:', error);
      throw error;
    }
  }

  /**
   * Get videos by required rank
   * @param {string} rank - Required rank
   * @returns {Promise} - API response with filtered videos
   */
  async getVideosByRank(rank) {
    try {
      return await this.getVideos({ rank });
    } catch (error) {
      console.error('Error fetching videos by rank:', error);
      throw error;
    }
  }

  /**
   * Search videos by text
   * @param {string} searchTerm - Search term
   * @returns {Promise} - API response with filtered videos
   */
  async searchVideos(searchTerm) {
    try {
      return await this.getVideos({ search: searchTerm });
    } catch (error) {
      console.error('Error searching videos:', error);
      throw error;
    }
  }

  /**
   * Validate video data before sending to API
   * @param {Object} videoData - Video data to validate
   * @returns {Object} - Validation result
   */
  validateVideoData(videoData) {
    const errors = [];
    
    if (!videoData.title || videoData.title.trim() === '') {
      errors.push('Title is required');
    }
    
    if (!videoData.description || videoData.description.trim() === '') {
      errors.push('Description is required');
    }
    
    if (!videoData.videoUrl || videoData.videoUrl.trim() === '') {
      errors.push('Video URL is required');
    }
    
    if (!videoData.category || videoData.category.trim() === '') {
      errors.push('Category is required');
    }
    
    if (!videoData.requiredRank || videoData.requiredRank.trim() === '') {
      errors.push('Required rank is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get available categories
   * @returns {Array} - List of available categories
   */
  getAvailableCategories() {
    return [
      'Început',
      'Echipă',
      'Business',
      'Ranguri',
      'Webinarii',
      'Masterclass',
      'Q&A Sessions'
    ];
  }

  /**
   * Get available ranks
   * @returns {Array} - List of available ranks
   */
  getAvailableRanks() {
    return [
      'basic',
      'team_member',
      'premier',
      'silver',
      'gold',
      'platinum',
      'diamond',
      'blue_diamond',
      'presidential_diamond'
    ];
  }

  /**
   * Get rank display names
   * @returns {Object} - Mapping of rank keys to display names
   */
  getRankDisplayNames() {
    return {
      basic: 'Cont de bază',
      team_member: 'Membru echipă',
      premier: 'Premier',
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
      diamond: 'Diamond',
      blue_diamond: 'Blue Diamond',
      presidential_diamond: 'Presidential Diamond'
    };
  }
}

// Export singleton instance
const videoService = new VideoService();
export default videoService;
